import { useState } from "react";
import { useNavigate } from "react-router-dom";

export default function ProductCard({ product }) {
  const navigate = useNavigate();
  const [imageError, setImageError] = useState(false);
  const [selectedCapacity, setSelectedCapacity] = useState(
    product.availableSizes ? product.availableSizes[0] : null
  );

  const handleImageError = () => {
    setImageError(true);
  };

  // Get current price based on selected capacity
  const currentPrice = selectedCapacity ? selectedCapacity.price : product.price;
  const currentOriginalPrice = selectedCapacity ? selectedCapacity.originalPrice : product.originalPrice;
  const currentDiscount = selectedCapacity ? selectedCapacity.discount : product.discount;

  // Helper function to map color names to hex codes
  const getColorHex = (colorName) => {
    const colorMap = {
      // Blacks & Grays
      'Black': '#000000',
      'Titanium Black': '#2C2C2E',
      'Titan Black': '#2C2C2E',
      'Titanium Jet Black': '#1C1C1C',
      'Graphite': '#3A3A3C',
      'Graphite Black': '#2C2C2C',
      'Awesome Graphite': '#3A3A3C',
      'Black Caviar': '#1C1C1C',
      'Deep Charcoal': '#36454F',
      'Dark Gray': '#4A4A4A',
      'Gray': '#808080',
      'Titanium Gray': '#6B7280',
      'Titan Gray': '#6B7280',
      'Lavender Gray': '#9CA3AF',
      'Awesome Lightgray': '#D1D5DB',
      'Blue Black': '#1E293B',

      // Whites & Silvers
      'White': '#FFFFFF',
      'Awesome White': '#FAFAFA',
      'Modern White': '#F8F8F8',
      'Silver': '#C0C0C0',
      'Titanium White Silver': '#E5E7EB',

      // Blues
      'Navy': '#1E3A8A',
      'Awesome Navy': '#1E3A8A',
      'Brushed Navy': '#1E40AF',
      'Titanium Silver Blue': '#60A5FA',
      'Light Blue': '#BFDBFE',
      'Iceblue': '#A5F3FC',
      'Awesome Iceblue': '#A5F3FC',

      // Purples & Violets
      'Violet': '#8B5CF6',
      'Awesome Violet': '#8B5CF6',
      'Light Violet': '#C4B5FD',
      'Lilac': '#C4B5FD',
      'Awesome Lilac': '#C4B5FD',
      'Bora Purple': '#A855F7',

      // Greens
      'Green': '#22C55E',
      'Mint': '#86EFAC',
      'Light Green': '#BBF7D0',
      'Titanium Jade Green': '#6EE7B7',
      'Lime': '#BEF264',
      'Awesome Lime': '#BEF264',
      'Awesome Olive': '#84CC16',

      // Yellows & Golds
      'Yellow': '#FCD34D',
      'Lemon': '#FEF08A',
      'Awesome Lemon': '#FEF08A',
      'Gold': '#F59E0B',
      'Pink Gold': '#E8B69A',
      'Titanium Pink Gold': '#E8B69A',
      'Sand Gold Metal': '#D4AF37',

      // Pinks
      'Peach Pink': '#FED7E2',
      'Awesome Pink': '#FCA5A5',

      // Browns & Beiges
      'Beige': '#F5E6D3',
      'Modern Brown': '#8B7355',
      'Modern Teak': '#B8956A',

      // Special Titanium Colors
      'Titanium': '#B4B4B4',
    };

    return colorMap[colorName] || '#CCCCCC';
  };

  // Use first color image if available, otherwise use default image
  const displayImage = product.colorImages && product.colorImages.length > 0
    ? product.colorImages[0]
    : product.image;

  return (
    <div
      className="product-item"
      onClick={() => navigate(`/product/${product.id}`)}
      style={{ cursor: 'pointer' }}
    >
      <div className="product-image">
        {imageError ? (
          <div className="image-placeholder">
            <p>{product.name}</p>
            <span>Image not available</span>
          </div>
        ) : (
          <img
            src={displayImage}
            alt={product.name}
            onError={handleImageError}
          />
        )}
      </div>
      <h3>{product.name}</h3>

      {/* Capacity/Size Selector for products with availableSizes - Hide for LEDs */}
      {product.availableSizes && product.availableSizes.length > 0 && product.category !== 'LEDs' && (
        <div className="capacity-selector" onClick={(e) => e.stopPropagation()}>
          <div className="capacity-options">
            {product.availableSizes.map((size, index) => (
              <button
                key={index}
                className={`capacity-btn ${selectedCapacity?.capacity === size.capacity ? 'active' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedCapacity(size);
                }}
              >
                {size.capacity}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Color Dots - Only show colors for non-LED products that have corresponding images */}
      {product.colors && product.colors.length > 0 && product.colorImages && product.category !== 'LEDs' && (
        <div className="product-card-colors">
          {product.colors.slice(0, Math.min(product.colorImages.length, 5)).map((color, index) => (
            <div
              key={index}
              className="color-dot"
              style={{
                backgroundColor: getColorHex(color),
                border: getColorHex(color) === '#FFFFFF' || getColorHex(color) === '#F8F8F8' ? '1.5px solid #ddd' : '1.5px solid rgba(0, 0, 0, 0.15)'
              }}
              title={color}
            />
          ))}
          {product.colorImages.length > 5 && (
            <span style={{ fontSize: '0.75rem', color: '#666' }}>+{product.colorImages.length - 5}</span>
          )}
        </div>
      )}

      <div className="product-specs">
        {product.ram && <span className="spec">RAM: {product.ram}</span>}
        {product.storage && <span className="spec">Storage: {product.storage}</span>}
        {product.size && <span className="spec">Size: {product.size}</span>}
        {product.channels && <span className="spec">{product.channels}</span>}
        {product.type && <span className="spec">{product.type}</span>}
      </div>

      <div className="product-price-section">
        <div className="price-info">
          <p className="current-price">PKR {currentPrice.toLocaleString()}</p>
          {currentDiscount > 0 && (
            <p className="original-price">PKR {currentOriginalPrice.toLocaleString()}</p>
          )}
        </div>
      </div>

      <button
        className="btn-add-to-cart"
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/product/${product.id}`);
        }}
      >
        Shop Now
      </button>
    </div>
  );
}
